import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Settings } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@heroui/react";
import { useMainStore } from "@/stores";
import { useAppMode } from "@/shared/hooks/useAppMode";
import { getMobileMenuItems } from "@/shared/utils/navigationConfig";
import { getUserDisplayName } from "@/shared/utils/userDisplayName";
import { USER_ROLES } from "@/shared/utils/constants";
import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import ModeToggle from "@/shared/components/ui/ModeToggle";
import LogoutConfirm from "@/shared/components/ui/Logout/LogoutConfirm";
import BrandLogo from "@/assets/images/logo.jpg";
import WhatsNewBell from "@/shared/components/WhatsNew/WhatsNewBell";

const ROLE_BADGE_LABELS = {
  [USER_ROLES.SUPER_ADMIN]: "Super Admin",
  [USER_ROLES.SUPER_ADMIN_ALT]: "Super Admin",
  [USER_ROLES.ORG_ADMIN]: "Org Admin",
  [USER_ROLES.USER]: "Member",
};

const MobileAppHeader = () => {
  const navigate = useNavigate();
  const { user } = useMainStore();
  const { isAdminMode, isOrgAdmin } = useAppMode();
  const [isOpen, setIsOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const menuItems = getMobileMenuItems({ user, isAdminMode, isOrgAdmin });
  const settingsItem = menuItems.find((item) => item.title === "Settings");
  const roleBadge = user?.role
    ? (ROLE_BADGE_LABELS[user.role] ?? "Member")
    : null;

  const handleSettings = () => {
    setIsOpen(false);
    navigate(settingsItem?.url || "/settings/profile_settings");
  };

  const handleLogout = () => {
    setIsOpen(false);
    setShowLogoutConfirm(true);
  };

  return (
    <>
      <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-gray-200 bg-white px-4 md:hidden">
        <div className="flex items-center gap-2">
          <img src={BrandLogo} alt="SalesWorx" width={17} height={31} />
          {roleBadge ? (
            <span className="rounded-md bg-secondary-transparent px-2 py-0.5 text-[10px] font-medium text-secondary">
              {roleBadge}
            </span>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
        <WhatsNewBell className="h-8 w-8" />
        <Popover
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          placement="bottom-end"
          offset={8}
        >
          <PopoverTrigger>
            <button
              type="button"
              aria-label="Open account menu"
              className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-secondary/40"
            >
              <CommonAvatar
                imageUrl={user?.profileImage}
                name={user?.name}
                className="h-9 w-9 rounded-full object-cover"
              />
            </button>
          </PopoverTrigger>

          <PopoverContent className="w-[min(280px,calc(100vw-2rem))] rounded-xl border border-gray-100 p-0 shadow-lg">
            <div className="w-full p-4">
              <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
                <CommonAvatar
                  imageUrl={user?.profileImage}
                  name={user?.name}
                  className="h-10 w-10 rounded-full object-cover"
                />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">
                    {getUserDisplayName(user)}
                  </p>
                  <p className="truncate text-[11px] text-gray-500">
                    {user?.email}
                  </p>
                </div>
              </div>

              {isOrgAdmin ? (
                <div className="border-b border-gray-100 py-3">
                  <ModeToggle />
                </div>
              ) : null}

              <div className="flex flex-col gap-1 pt-3">
                {settingsItem ? (
                  <button
                    type="button"
                    onClick={handleSettings}
                    className="flex items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <Settings size={16} className="text-gray-600" />
                    Settings
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <LogOut size={16} className="text-gray-600" />
                  Log out
                </button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        </div>
      </header>

      {showLogoutConfirm ? (
        <LogoutConfirm
          showLogoutConfirm={showLogoutConfirm}
          onClose={() => setShowLogoutConfirm(false)}
        />
      ) : null}
    </>
  );
};

export default MobileAppHeader;
